import React from 'react';
import { useParams } from 'react-router-dom';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Comment, Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { getFormattedTime } from 'flow/utils';
import { getStyledMessageBody } from 'flow/utils/message-utils';
import { twMerge } from 'tailwind-merge';
import FromAvatar from './FromAvatar';
import MessageSender from './MessageSender';

interface Props {
    message: TypedMap<Message>;
    sessionKey: ChatSessionKey;
}

const ChatPostCommentsList = ({ message, sessionKey }: Props) => {
    // While the comments are in the message, they are a snapshot and will become outdated if new
    // comments are posted, so we need to set up a selector to receive updates to comments.
    const latestMessages = useAppSelector((state) =>
        state.chats.getIn(['messages', sessionKey]),
    );
    const latestMessage = latestMessages?.find(
        (m) => m.get('key') === message.get('key'),
    );
    const comments =
        latestMessage?.get('chatComments') || message.get('chatComments');

    if (comments.count() === 0) {
        return null;
    }
    return (
        <div className="flex flex-col gap-y-6 border-t p-6">
            {comments.map((comment) => (
                <CommentDisplay
                    key={comment.get('key')}
                    comment={comment}
                    sessionKey={sessionKey}
                />
            ))}
        </div>
    );
};

const CommentDisplay = ({
    comment,
    sessionKey,
}: {
    comment: TypedMap<Comment>;
    sessionKey: ChatSessionKey;
}) => {
    const { i18next } = useAppContext();
    const { id } = useParams<{ id: ChatSessionKey }>();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const sent = comment.get('sent');

    return (
        <div className="flex gap-x-4">
            <div className="mt-1">
                <FromAvatar
                    from={comment.get('from')}
                    avatarOnly={true}
                    chatSessionKey={id || sessionKey}
                />
            </div>
            <div className="flex flex-1 flex-col gap-y-1">
                <div className="flex">
                    <div
                        className={twMerge(
                            'flex-1',
                            comment.getIn(['from', 'extensionKey']) !==
                                ownExtensionKey && 'cursor-pointer',
                        )}
                    >
                        <MessageSender
                            message={comment}
                            sessionKey={sessionKey || id}
                        />
                    </div>
                    <time className="text-xs/4 text-gray-500" dateTime={sent}>
                        {getFormattedTime(sent, i18next)}
                    </time>
                </div>
                <p
                    dir="auto"
                    className="leading-6 wrap-break-word whitespace-pre-wrap"
                >
                    {getStyledMessageBody(
                        comment,
                        sessionKey,
                        false, // Disable markdown for comments
                    )}
                </p>
            </div>
        </div>
    );
};

export default ChatPostCommentsList;
